import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface EmptyStateProps {
  /** What is not here. A noun phrase, not "No data". */
  title: string;
  /** Why it is not here, and what would put something here. */
  description?: ReactNode;
  icon?: IconName;
  /** The one action that resolves the emptiness, if there is one. */
  action?: ReactNode;
  /** Inside a card that already has a border, drop this one. */
  bare?: boolean;
  className?: string;
}

/**
 * An empty state is a screen the user WILL see - on day one every list here
 * is empty - so it gets the same care as the populated one. It says what is
 * missing, why, and what to do, and it never says "No data available".
 */
export function EmptyState({
  title,
  description,
  icon = 'inbox',
  action,
  bare = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        !bare && 'rounded-lg border border-dashed border-line-strong bg-surface/60',
        className,
      )}
    >
      <span className="grid size-11 place-items-center rounded-full border border-line bg-sunken text-subtle">
        <Icon name={icon} size={19} />
      </span>
      <div className="max-w-sm">
        <h3 className="font-display text-section font-semibold tracking-tight">{title}</h3>
        {description ? <p className="mt-1 text-small text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}
